// bridge.js — resolves the per-route bootstrap payload and (re)installs
// window.OPDBridge before a screen module is imported.
//
// ONLINE-PRIMARY (main-2): routes listed in MEDPLUM_LOADERS read Medplum
// directly (FHIR-direct + client-side shaping, no view Bot) as the logged-in
// user — RLS is the only authorization lock. Same DTOs as the old Express
// view-adapter, so the screen JSX is unchanged. Converted routes are
// Medplum-ONLY and FAIL LOUD (the Express fallback was deleted 2026-07-02);
// offline resilience on the list boards comes from the SWR cache, not Express.
//
// The Express view-bootstrap endpoint is now FULLY RETIRED (server
// demolition): magic-link anon surfaces (intake/portal/anon-encounter WITH a
// token) boot via the RLS-native chart-read Bot loaders (medplum-intake/
// medplum-portal); everything that used to reach the server "fallback"
// (token-display, which self-loads Medplum-native, and the token-LESS
// intake/portal demo preview) now installs a meta-only bootstrap built
// client-side (medplum-previewboot.js). No route fetches the Express endpoint.
import { installBridge } from './bridge-core.js';
import { loadQueueFromMedplum } from './medplum-queue.js';
import { loadReceptionFromMedplum } from './medplum-reception.js';
import { loadPatientsFromMedplum } from './medplum-patients.js';
import { loadScheduleFromMedplum } from './medplum-schedule.js';
import { loadReportsFromMedplum } from './medplum-reports.js';
import { loadEncounterFromMedplum } from './medplum-encounter.js';
import { loadSettingsFromMedplum } from './medplum-settings.js';
import { swrLoad, startLiveUpdates } from './medplum-live.js';
import { getMedplum } from './medplum-client.js';
import { loadIntakeBoot, loadAnonEncounterBoot } from './medplum-intake.js';
import { loadPortalBoot } from './medplum-portal.js';
import { buildPreviewBoot } from './medplum-previewboot.js';
import { OPD_LOCAL_EVENT } from './opd-events.js';
import { warmSwrBoards } from './swr-warm.js';

const MEDPLUM_LOADERS = {
  queue: loadQueueFromMedplum,
  reception: loadReceptionFromMedplum,
  patients: loadPatientsFromMedplum,
  schedule: loadScheduleFromMedplum,
  reports: loadReportsFromMedplum,
  encounter: loadEncounterFromMedplum,
  // 'settings' bootstraps /settings AND /tasks /messaging /spaces /automations
  // (they need only currentUser/hospital from it — their data is in-screen
  // loaders). Not in SWR_ROUTES/LIVE_CRITERIA: Settings is load-once + Retry.
  settings: loadSettingsFromMedplum,
};

// ONE real Medplum session per browser (the cutover): every converted staff
// route reads as the logged-in user via getMedplum() — the per-route identity
// multiplexing (ROUTE_IDENTITY/identityFor: doctor/receptionist/admin demo
// slots) is GONE. Whether the logged-in user may see a route is App.jsx's
// role gate (role derived from their Medplum resources), and whether they can
// read the data is RLS — a receptionist route opened by a doctor bounces at
// the role gate; a route they may see returns only rows their AccessPolicy
// allows.

// SWR (stale-while-revalidate) is for the read-only LIST boards: paint the
// last authorized read instantly, refetch behind. The consult ('encounter')
// route is deliberately EXCLUDED — an actively-edited clinical chart must
// never paint stale (a doctor acting on an outdated draft/vitals for even a
// second is a clinical hazard; If-Match would catch the write, but the read
// itself must be fresh).
const SWR_ROUTES = new Set(['queue', 'reception', 'patients', 'schedule', 'reports']);

// Medplum WS subscription criteria per route — the resources whose changes
// should nudge this board. RLS scopes the subscription server-side (a doctor
// only receives events for rows their AccessPolicy lets them read — verified
// e2e incl. the cross-department negative). The consult route gets NO live
// nudge: it is an active editing surface, not a board.
const LIVE_CRITERIA = {
  queue: ['Encounter'],
  reception: ['Encounter'],
  schedule: ['Appointment', 'Encounter'],
  patients: ['Patient', 'Encounter'],
  reports: ['DocumentReference'],
};

// (The queue soft-reload hack that lived here is retired: Queue.jsx now has
// its own in-place state path — useState seeded from the module const + a
// subscribeOpdEvents(['Encounter']) effect fed by the OPD_LOCAL_EVENT this
// file dispatches — so every board repaints in place; no full-page reloads.)

// PER-ROUTE live wire (design §1c). Under SPA nav loadBridge runs on every URL
// change within one document, so a single "one wire per page load" boolean is
// wrong twice over: (a) only the FIRST route would ever get a subscription; and
// (b) a previous board's wire firing while you're on /consult would run its
// revalidate → installBridge and CLOBBER the consult bridge with a queue
// payload. So we track which route the current wire belongs to, retire it when
// the route changes, and guard the background revalidate against a stale route.
let liveRoute = null;      // route the current wire belongs to
let liveCleanup = null;    // startLiveUpdates' returned cleanup
let activeRoute = null;    // route of the most recent loadBridge call

function retireLiveWire(nextRoute) {
  if (liveRoute === nextRoute) return;        // same board re-visit: keep the socket
  if (liveCleanup) { try { liveCleanup(); } catch (_) {} }
  liveCleanup = null; liveRoute = null;
}

function startLiveForRoute(route, revalidate) {
  if (liveRoute === route) return;            // already wired for this board
  const criteria = LIVE_CRITERIA[route];
  if (!criteria) return;
  liveRoute = route;
  getMedplum().then(({ medplum }) => {
    if (liveRoute !== route) return;          // navigated away while resolving
    liveCleanup = startLiveUpdates({
      medplum,
      criteria,
      onEvent: (payload) => {
        // 1. Bust MedplumClient's in-memory read cache for the changed type —
        //    its default 60s request cache would otherwise serve the SAME
        //    stale search result to the revalidate below and mask the change
        //    entirely (the exact gotcha REDESIGN_ONLINE_PRIMARY.md documents).
        try { medplum.invalidateSearches(payload.type); } catch (_) {}
        // 2. Nudge any mounted opd-events consumer (Reception's quiet
        //    refreshToday, NurseStation, Command...) — same payload shape the
        //    SSE line emits, delivered through the same debounce path.
        try { window.dispatchEvent(new CustomEvent(OPD_LOCAL_EVENT, { detail: payload })); } catch (_) {}
        // 3. Revalidate this route's SWR cache so the next paint is fresh —
        //    and, for the idle queue board only, soft-reload when it differs.
        revalidate();
      },
    });
  }).catch(() => { /* no session → no live wire; the loader already failed loudly */ });
}

// The 'encounter' bootstrap serves BOTH the staff consult room (/consult/*)
// AND anonymous patient surfaces (/kiosk, /tablet-intake/:id, /saathi/:id).
// The Medplum-direct path runs as the LOGGED-IN STAFF identity — an anonymous
// kiosk tablet must NEVER ride it (AGENTS.md §2: never widen an anonymous
// surface; the redesign gives anon surfaces Bots/thin-gateway instead). Only
// the consult room takes the Medplum path; anon routes stay on their
// magic-link/paired-device Express flow.
function medplumPathAllowed(route) {
  if (route !== 'encounter') return true;
  try { return window.location.pathname.startsWith('/consult/'); } catch { return false; }
}

// ── ANON surfaces (main-2, Wave-3A): magic-link boot loaders ──────────────────
// The anonymous patient surfaces (/intake·/saathi = 'intake'; /portal·/summary
// = 'portal'; /tablet-intake/:id·/saathi/:id = anon 'encounter') read Medplum
// through the execute-only chart-read Bot as a POWERLESS anon client — NEVER the
// logged-in staff session (AGENTS.md §2: never widen an anonymous surface). We
// take this RLS-native path ONLY when a magic-link {token} is present. Without a
// token these routes fall through to the Express bootstrap below: token-less
// /intake·/portal is the demo/preview affordance (server returns meta-only → the
// screens' own demo fallbacks render), and the device-cookie kiosk path
// (/tablet-intake with no query token) keeps its Express boot until its own
// device-token cutover.
function anonEncounterPath() {
  try {
    const p = window.location.pathname;
    return p.startsWith('/tablet-intake/') || p.startsWith('/saathi/');
  } catch { return false; }
}

function anonLoaderFor(route, token) {
  if (!token) return null;
  if (route === 'intake') return loadIntakeBoot;
  if (route === 'portal') return loadPortalBoot;
  if (route === 'encounter' && anonEncounterPath()) return loadAnonEncounterBoot;
  return null;
}

export async function loadBridge(route, { id = '', token = '', lang = '', patientId = '' } = {}) {
  // Runs for EVERY route (incl. non-live ones like settings/consult), so a stale
  // board wire can never fire under a different screen: mark this route active
  // and retire any wire that belongs to a different board (design §1c).
  activeRoute = route;
  retireLiveWire(route);
  // ── ONLINE-PRIMARY (main-2) routes: Medplum-only, FAIL LOUD ──────────────
  // NO Express fallback on converted routes (owner decision 2026-07-02): a
  // Medplum failure throws and App.jsx's ScreenBoot renders the designed
  // error state. Silently swapping to a different data source would hide
  // outages and could paint different/staler data than what the clinician
  // thinks they're seeing (AGENTS.md §1.3 — unneeded fallbacks hide bugs).
  // Offline reads are the SWR cache's job, not Express's.
  const medplumLoader = MEDPLUM_LOADERS[route];
  if (medplumLoader && medplumPathAllowed(route)) {
    const loader = () => (route === 'encounter' ? medplumLoader({ id, patientId }) : medplumLoader({ id }));
    if (SWR_ROUTES.has(route)) {
      // swrLoad's cache key folds in the REAL logged-in profile id (see
      // medplum-live.js cacheKeyFor) — with one session per browser that id is
      // the whole per-user isolation; the legacy identity slot is left to its
      // default.
      const revalidate = () => swrLoad(route, loader, {
        onRevalidate: (fresh) => {
          // Stale-route guard (design §1c): an in-flight background read from
          // route X can resolve AFTER the user navigated to Y. swrLoad already
          // updated the cache for X; do NOT install it over Y's live bridge.
          if (activeRoute !== route) return;
          // Swap in the fresh payload + re-install the bridge so any screen
          // reading window.OPDBridge in render/effect sees it (the boards'
          // own in-place effects handle the visible repaint).
          window.__OPD_DATA__ = fresh;
          installBridge(window);
        },
      });
      window.__OPD_DATA__ = await revalidate();
      startLiveForRoute(route, revalidate);
      // First paint is done (the payload above is what the screen will render).
      // Now fire-and-forget the OTHER boards so the next nav is a warm hit. Do
      // it on the next tick so it never delays this route's install/paint; the
      // consult route is never a warm source (it's not in WARM_TARGETS) and the
      // active board is skipped inside warmSwrBoards.
      setTimeout(() => { try { warmSwrBoards(route); } catch (_) {} }, 0);
    } else {
      // encounter (consult/prescription) + settings: ALWAYS a fresh read — no
      // SWR (see SWR_ROUTES/LIVE_CRITERIA notes above).
      window.__OPD_DATA__ = await loader();
    }
    return installBridge(window);
  }

  // ── ANON surfaces: RLS-native magic-link boot (main-2, Wave-3A) ──────────
  // intake / portal / anon-encounter with a magic-link token → chart-read Bot →
  // client shaping (buildEncounterView) → the exact __OPD_DATA__ shape. Medplum-
  // only + FAIL LOUD, like the staff loaders above (the anon Bot verifies the
  // token fail-closed). Portal maps only the Bot's tagged token refusal to its
  // designed expired/invalid-link payload; transport/configuration errors still
  // throw. No Express fallback on the converted magic-link leg.
  const anonLoader = anonLoaderFor(route, token);
  if (anonLoader) {
    window.__OPD_DATA__ = await anonLoader({ id, token, lang, patientId });
    return installBridge(window);
  }

  // ── Last leg: token-display (self-loading) + token-less preview ──────────────
  // The former Express view-bootstrap fallback is DELETED (server demolition).
  // A census of the routes that reached it showed none still needs a server
  // payload (full rationale + parity note in medplum-previewboot.js):
  //   • token-display → the screen SELF-LOADS the same-origin no-PHI board feed
  //     (medplum-tokendisplay.js) and ignores this payload; it
  //     only needs a bridge object to mount.
  //   • token-LESS intake / portal / anon-encounter → the demo/PREVIEW affordance:
  //     with no magic-link token there is no patient to read, so the screens render
  //     their own demo fallbacks off installBridge()'s `pick` (a meta-only payload
  //     carries NO chart root → pick serves the demo, isLive() is false). A real
  //     magic-link WITH a token never reaches here (anonLoaderFor above claims it).
  //   • the legacy 'admin' bootstrap is gone — every admin screen boots 'settings'
  //     Medplum-direct now (App.jsx has no `bootstrap: 'admin'`).
  // So build the minimal meta-only bootstrap CLIENT-SIDE — no Express, no auth.
  window.__OPD_DATA__ = buildPreviewBoot(route, { id, token, lang });
  return installBridge(window);
}
